import React, { useEffect } from "react";
import EnhancedSelect from "../../EnhancedSelect";
import { FilterDialog, getOperators } from "../../table";
import { StyledFilterControl, StyledFilterSelector } from "../catalog_filters";
import { useTheme } from '@mui/material/styles';
import FilterField from "./components/FilterField";
import { getPartnerGroups } from '../../../helpers';

interface OperatorOption {
  op: string;
  label: string;
}

type FilterValue = string | number | boolean | null | string[] | [string, string] | Record<string, unknown>;

interface FilterItem {
  field?: string;
  operator?: string;
  value?: FilterValue;
  static?: boolean;
  filters?: FilterItem[];
}

interface ColumnOption {
  id: string;
  label: string;
  type?: string;
  hidden?: boolean;
  toggleable?: boolean;
  filterable?: boolean;
  defaultFilterOperator?: string;
  getOptions?: (allData: unknown[]) => Promise<Array<{ label: string; value?: unknown }>>;
}

type SelectOption = { value: unknown; label: string; title?: string };

interface TableLike {
  state: {
    allData?: unknown[];
  };
}

interface CatalogFilterProps {
  filter: FilterItem;
  columns: ColumnOption[];
  detailedFilters: FilterItem[];
  setDetailedFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>;
  index: number;
  table: TableLike;
}

function CatalogFilter(props: CatalogFilterProps) {
  const { filter, columns } = props;
  const theme = useTheme();
  const [operators, setOperators] = React.useState<OperatorOption[] | undefined>(undefined);
  const [options, setOptions] = React.useState<Array<{ label: string; value?: unknown }> | undefined>(undefined);
  const [type, setType] = React.useState<string | undefined>(undefined);

  useEffect(() => {
    const startFilter = async () => {
      if (filter.field !== "" && !filter.static && !filter.filters) {
        let column = columns.find((col) => col.id === filter.field);
        if (!column) {
          console.warn(`Column with id "${filter.field}" not found in current table columns`);
          return;
        }

        let nextOperators = getOperators(column) as OperatorOption[];
        let nextOptions: Array<{ label: string; value?: unknown }> | undefined = undefined;
        if (column.getOptions !== undefined) {
          if (nextOperators.filter((r) => r.op === 'ingroup').length > 0) {
            const groups = await getPartnerGroups();
            if (groups.length === 0) {
              nextOperators = nextOperators.filter((r) => r.op !== "ingroup");
            }
          }
          if (filter.operator === 'ingroup') {
            nextOptions = (await getPartnerGroups()).map((r: { label: string }) => ({ label: r.label, value: r.label }));
          } else {
            nextOptions = await column.getOptions(props.table.state.allData || []);
          }
        }
        setOperators(nextOperators);
        setOptions(nextOptions);
        setType(column.type || "text");
      }
    };

    startFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.detailedFilters]);

  return (
    <FilterDialog columns={columns}
      addFilter={(newFilter: FilterItem) => props.setDetailedFilters([...props.detailedFilters, newFilter])}
      template={({ state, extensions }: { state: { type?: string; loadingOptions?: boolean }; extensions: { handleValueChange: (e: { target: { value: unknown } } | string) => void; handleFilterChange: (e: { target: { value: unknown } }) => void; handleOpChange: (e: { target: { value: unknown } }) => void } }) => {
        let foundColumn = props.columns.find((column) => column.id === filter.field);

        const onFilterChange = (event: { value: string }) => {
          const newEvent = { target: { value: event.value } };
          extensions.handleValueChange("");
          extensions.handleFilterChange(newEvent);

          const currentFilter = props.detailedFilters[props.index];
          currentFilter.field = event.value;
          currentFilter.operator = "";
          currentFilter.value = "";

          foundColumn = props.columns.find((column) => column.id === event.value);
          if (foundColumn && foundColumn.defaultFilterOperator) {
            onOperatorChange({ value: foundColumn.defaultFilterOperator });
          } else {
            props.setDetailedFilters([...props.detailedFilters]);
          }
        };

        const onOperatorChange = (event: { value: string }) => {
          const newEvent = { target: { value: event.value } };
          extensions.handleOpChange(newEvent);

          const currentFilter = props.detailedFilters[props.index];
          currentFilter.operator = event.value;
          props.setDetailedFilters([...props.detailedFilters]);
        };

        const onValueChange = (event: { value: FilterValue }) => {
          const newEvent = { target: { value: event.value } };
          extensions.handleValueChange(newEvent);

          const currentFilter = props.detailedFilters[props.index];
          currentFilter.value = event.value;
          props.setDetailedFilters([...props.detailedFilters]);
        };

        return (
          <>
            <EnhancedSelect placeholder={"Field"}
              isClearable
              value={filter.field === "" ? "" : foundColumn === undefined ? "" : {
                value: foundColumn.id,
                label: foundColumn.label
              }}
              options={columns.filter((column) =>
                (column.filterable === undefined || column.filterable !== false)
                && (!column.hidden || (column.hidden && column.toggleable))).map((column) => ({
                  value: column.id,
                  label: column.label,
                  title: column.label
                })) as SelectOption[]}
              onChange={(e: { value: string } | null) => onFilterChange(e || { value: "" })}
              containerStyle={{ flex: 0.35, minWidth: "180px" }}
              selectorStyle={StyledFilterSelector}
              controlStyle={StyledFilterControl}
              placeholderStyle={{ color: theme.palette.textPrimary.main }}
              dropdownIndicatorStyle={{ color: theme.palette.textPrimary.main }} />

            <EnhancedSelect placeholder={"Operator"}
              isClearable
              value={(filter.operator === "" || operators == null) ? "" : {
                value: filter.operator,
                label: operators.find((operator) => operator.op === filter.operator) === undefined ? ""
                  : (operators.find((operator) => operator.op === filter.operator)?.label || "")
              }}
              options={operators === undefined ? [] : operators.map((operator) => ({
                value: operator.op,
                label: operator.label,
                title: operator.label
              })) as SelectOption[]}
              onChange={(e: { value: string } | null) => onOperatorChange(e || { value: "" })}
              containerStyle={{ flex: 0.25, minWidth: "160px" }}
              selectorStyle={StyledFilterSelector}
              controlStyle={StyledFilterControl}
              placeholderStyle={{ color: theme.palette.textPrimary.main }}
              dropdownIndicatorStyle={{ color: theme.palette.textPrimary.main }}
              menuHeight={9999} />

            <FilterField type={type || state.type} value={filter.value}
              onValueChange={onValueChange}
              options={options} loadingOptions={state.loadingOptions}
              operator={filter.operator} />

          </>
        );
      }} />
  );
}

export default CatalogFilter;
